import type { ElementType, ReactNode } from "react"
import React, { forwardRef, useCallback, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { ChevronDownIcon } from "../icons"
import { useZIndex } from "@shared/hooks/useZIndex"

// Типы для пропсов
export interface CDropdownProps<T extends ElementType = "div"> {
  // Основные пропсы
  children?: ReactNode
  label?: ReactNode
  trigger?: "click" | "hover"
  placement?:
    | "top"
    | "right"
    | "bottom"
    | "left"
    | "top-start"
    | "top-end"
    | "right-start"
    | "right-end"
    | "bottom-start"
    | "bottom-end"
    | "left-start"
    | "left-end"
  inline?: boolean
  arrowIcon?: ReactNode
  dismissOnClick?: boolean
  disabled?: boolean
  color?: "gray" | "info" | "failure" | "success" | "warning"
  theme?: Record<string, unknown>
  className?: string
  contentClassName?: string // Стили для контента dropdown

  // Состояние
  open?: boolean
  onOpenChange?: (open: boolean) => void

  // Кастомные элементы
  renderTrigger?: (props: { ref: React.Ref<HTMLElement>; onClick: () => void; disabled?: boolean }) => ReactNode

  // HTML пропсы
  as?: T
  id?: string
  style?: React.CSSProperties
  onClick?: (event: React.MouseEvent) => void
  onMouseEnter?: (event: React.MouseEvent) => void
  onMouseLeave?: (event: React.MouseEvent) => void
}

// Типы для элементов dropdown
export interface CDropdownItemProps {
  children?: ReactNode
  icon?: ReactNode
  onClick?: (event: React.MouseEvent) => void
  disabled?: boolean
  as?: ElementType
  className?: string
  style?: React.CSSProperties
}

export interface CDropdownHeaderProps {
  children?: ReactNode
  className?: string
  style?: React.CSSProperties
}

export interface CDropdownDividerProps {
  className?: string
  style?: React.CSSProperties
}

// Компонент Dropdown Item
export const CDropdownItem = forwardRef<HTMLButtonElement, CDropdownItemProps>((props, ref) => {
  const { children, icon, onClick, disabled = false, as: Component = "button", className = "", style, ...rest } = props

  const handleClick = (event: React.MouseEvent) => {
    if (disabled) return
    onClick?.(event)
  }

  const baseClasses = "flex items-center justify-start py-2 px-4 text-sm text-gray-700 w-full whitespace-nowrap"
  const interactiveClasses = disabled ? "cursor-default text-gray-500" : "cursor-pointer hover:bg-gray-100"
  const finalClasses = `${baseClasses} ${interactiveClasses} ${className}`

  return (
    <Component
      ref={ref}
      className={finalClasses}
      style={style}
      onClick={handleClick}
      disabled={disabled}
      {...rest}
    >
      {icon && <span className="mr-2 h-4 w-4">{icon}</span>}
      {children}
    </Component>
  )
})

CDropdownItem.displayName = "CDropdownItem"

// Компонент Dropdown Header
export const CDropdownHeader = forwardRef<HTMLDivElement, CDropdownHeaderProps>((props, ref) => {
  const { children, className = "", style, ...rest } = props

  return (
    <div
      ref={ref}
      className={`block py-2 px-4 text-sm text-gray-700 ${className}`}
      style={style}
      {...rest}
    >
      {children}
    </div>
  )
})

CDropdownHeader.displayName = "CDropdownHeader"

// Компонент Dropdown Divider
export const CDropdownDivider = forwardRef<HTMLDivElement, CDropdownDividerProps>((props, ref) => {
  const { className = "", style, ...rest } = props

  return (
    <div
      ref={ref}
      className={`my-1 h-px bg-gray-100 ${className}`}
      style={style}
      {...rest}
    />
  )
})

CDropdownDivider.displayName = "CDropdownDivider"

// Основной компонент CDropdown
const CDropdownComponent = forwardRef<HTMLDivElement, CDropdownProps>((props, ref) => {
  const {
    children,
    label,
    trigger = "click",
    placement = "bottom",
    inline = false,
    arrowIcon = true,
    dismissOnClick = true,
    disabled = false,
    color = "gray",
    theme,
    className = "",
    contentClassName = "",
    open: controlledOpen,
    onOpenChange,
    renderTrigger,
    as: Component = "div",
    id,
    style,
    onClick,
    onMouseEnter,
    onMouseLeave,
    ...rest
  } = props

  // Состояние
  const [isOpen, setIsOpen] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  // Рефы
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)

  // Определяем, контролируемый ли компонент
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : isOpen

  // Динамический z-index для правильного наложения при множественных dropdown
  const zIndex = useZIndex(open)

  // Обработчики событий
  const handleToggle = useCallback(() => {
    if (disabled) return

    const newOpen = !open
    if (!isControlled) {
      setIsOpen(newOpen)
    }
    onOpenChange?.(newOpen)
  }, [open, disabled, isControlled, onOpenChange])

  const handleClose = useCallback(() => {
    if (disabled) return

    if (!isControlled) {
      setIsOpen(false)
    }
    onOpenChange?.(false)
  }, [disabled, isControlled, onOpenChange])

  const handleMouseEnter = useCallback(() => {
    if (trigger === "hover" && !disabled) {
      setIsHovered(true)
      if (!isControlled) {
        setIsOpen(true)
      }
      onOpenChange?.(true)
    }
  }, [trigger, disabled, isControlled, onOpenChange])

  const handleMouseLeave = useCallback(() => {
    if (trigger === "hover" && !disabled) {
      setIsHovered(false)
      // Небольшая задержка для плавного перехода
      setTimeout(() => {
        if (!isHovered) {
          if (!isControlled) {
            setIsOpen(false)
          }
          onOpenChange?.(false)
        }
      }, 100)
    }
  }, [trigger, disabled, isControlled, onOpenChange, isHovered])

  const handleItemClick = useCallback(() => {
    handleClose()
  }, [handleClose])

  // Обработчик клика вне компонента
  useEffect(() => {
    if (!open) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      // Проверяем, что клик не внутри триггера или dropdown
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        handleClose()
      }
    }

    // Обработчик клавиши Escape. Capture + preventDefault: этот Escape
    // закрывает ТОЛЬКО дропдаун — модал под ним (CModalCore) видит пометку
    // defaultPrevented и не закрывает всю панель тем же нажатием.
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault()
        handleClose()
      }
    }

    document.addEventListener("mousedown", handleClickOutside, true)
    document.addEventListener("keydown", handleEscape, true)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true)
      document.removeEventListener("keydown", handleEscape, true)
    }
  }, [open, handleClose])

  // Состояние для позиции dropdown и готовности к показу
  const [dropdownPosition, setDropdownPosition] = useState({ top: "0px", left: "0px" })
  const [triggerWidth, setTriggerWidth] = useState(0)
  const [isPositioned, setIsPositioned] = useState(false)

  // Вычисляем позицию dropdown с реальными размерами
  // Wrapped so its identity only changes with `placement`, which is all it reads besides two refs.
  // A fresh closure every render is what forced the two effects below to hide it behind a
  // suppression — and an inline eslint-disable of a react-hooks rule makes the React Compiler skip
  // the whole component, so the suppression cost far more than the dependency it hid.
  const getDropdownPosition = useCallback(() => {
    if (!triggerRef.current || !dropdownRef.current) return {}

    const triggerRect = triggerRef.current.getBoundingClientRect()
    const dropdownRect = dropdownRef.current.getBoundingClientRect()

    let top = 0
    let left = 0

    switch (placement) {
      case "top":
        top = triggerRect.top - dropdownRect.height - 8
        left = triggerRect.left + (triggerRect.width - dropdownRect.width) / 2
        break
      case "top-start":
        top = triggerRect.top - dropdownRect.height - 8
        left = triggerRect.left
        break
      case "top-end":
        top = triggerRect.top - dropdownRect.height - 8
        left = triggerRect.right - dropdownRect.width
        break
      case "right":
        top = triggerRect.top + (triggerRect.height - dropdownRect.height) / 2
        left = triggerRect.right + 8
        break
      case "right-start":
        top = triggerRect.top
        left = triggerRect.right + 8
        break
      case "right-end":
        top = triggerRect.bottom - dropdownRect.height
        left = triggerRect.right + 8
        break
      case "bottom":
        top = triggerRect.bottom + 8
        left = triggerRect.left + (triggerRect.width - dropdownRect.width) / 2
        break
      case "bottom-start":
        top = triggerRect.bottom + 8
        left = triggerRect.left
        break
      case "bottom-end":
        top = triggerRect.bottom + 8
        left = triggerRect.right - dropdownRect.width
        break
      case "left":
        top = triggerRect.top + (triggerRect.height - dropdownRect.height) / 2
        left = triggerRect.left - dropdownRect.width - 8
        break
      case "left-start":
        top = triggerRect.top
        left = triggerRect.left - dropdownRect.width - 8
        break
      case "left-end":
        top = triggerRect.bottom - dropdownRect.height
        left = triggerRect.left - dropdownRect.width - 8
        break
      default:
        top = triggerRect.bottom + 8
        left = triggerRect.left + (triggerRect.width - dropdownRect.width) / 2
    }

    // Проверяем границы экрана
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    if (left < 0) left = 8
    if (left + dropdownRect.width > viewportWidth) left = viewportWidth - dropdownRect.width - 8
    if (top < 0) top = 8
    if (top + dropdownRect.height > viewportHeight) top = viewportHeight - dropdownRect.height - 8

    return { top: `${top}px`, left: `${left}px` }
  }, [placement])

  // Позиционируем dropdown после рендера портала.
  // animationFillMode: backwards на элементе гарантирует, что from-keyframe
  // (opacity 0) применяется ДО старта анимации — без мелькания в (0,0).
  useEffect(() => {
    if (open && triggerRef.current) {
      setIsPositioned(false)

      const triggerRect = triggerRef.current.getBoundingClientRect()
      setTriggerWidth(triggerRect.width)

      const rafId = requestAnimationFrame(() => {
        if (dropdownRef.current) {
          const position = getDropdownPosition()
          if (position.top && position.left) {
            setDropdownPosition(position)
            setIsPositioned(true)
          }
        }
      })

      return () => cancelAnimationFrame(rafId)
    }
    setIsPositioned(false)
  }, [open, placement, getDropdownPosition])

  // Re-run the viewport clamp whenever the dropdown's rendered size changes —
  // e.g. when a collapsible section inside it expands. Without this, a menu
  // pinned near the bottom of the screen would grow past the viewport when
  // the "Delete" submenu expands.
  useEffect(() => {
    if (!open || !dropdownRef.current) return
    const el = dropdownRef.current
    const observer = new ResizeObserver(() => {
      const position = getDropdownPosition()
      if (position.top && position.left) {
        setDropdownPosition(position)
        // Позиция посчитана — значит показывать уже можно. Раньше флаг ставился только в
        // requestAnimationFrame выше, и если там реф ещё не был готов (getDropdownPosition
        // вернул пустой объект), меню оставалось с opacity-0 и pointer-events-none навсегда:
        // на экране его не видно, а клики уходили в подложку и закрывали его.
        setIsPositioned(true)
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [open, getDropdownPosition])

  // Базовые классы - используем w-max когда нет стрелки и кастомный label
  const isCustomLabel = typeof label !== "string"
  const useCompactWidth = !arrowIcon && isCustomLabel
  const baseClasses = useCompactWidth ? "relative" : "relative w-full"
  const finalClasses = `${baseClasses} ${className}`

  // Получаем направление slide анимации на основе placement
  const getSlideDirection = () => {
    if (placement.startsWith("top")) return "slide-in-from-bottom-2"
    if (placement.startsWith("bottom")) return "slide-in-from-top-2"
    if (placement.startsWith("left")) return "slide-in-from-right-2"
    if (placement.startsWith("right")) return "slide-in-from-left-2"
    return "slide-in-from-top-2"
  }

  // Получаем transform-origin на основе placement для корректной анимации zoom
  const getTransformOrigin = () => {
    switch (placement) {
      case "top":
        return "bottom center"
      case "top-start":
        return "bottom left"
      case "top-end":
        return "bottom right"
      case "bottom":
        return "top center"
      case "bottom-start":
        return "top left"
      case "bottom-end":
        return "top right"
      case "left":
        return "center right"
      case "left-start":
        return "top right"
      case "left-end":
        return "bottom right"
      case "right":
        return "center left"
      case "right-start":
        return "top left"
      case "right-end":
        return "bottom left"
      default:
        return "top left"
    }
  }

  // Классы для dropdown контента (divide-y убран, т.к. используются явные CDropdownDivider)
  const dropdownBaseClasses = "rounded-xl shadow-lg overflow-y-auto"
  const dropdownAnimationClasses = isPositioned
    ? `animate-in fade-in-0 zoom-in-95 ${getSlideDirection()} duration-200`
    : "opacity-0 invisible pointer-events-none"
  const dropdownHiddenClasses = open ? "visible" : "invisible"

  const dropdownColorClasses = {
    gray: "border border-gray-200 bg-white text-gray-900",
    info: "border border-blue-200 bg-blue-50 text-blue-900",
    failure: "border border-red-200 bg-red-50 text-red-900",
    success: "border border-green-200 bg-green-50 text-green-900",
    warning: "border border-yellow-200 bg-yellow-50 text-yellow-900",
  }

  const dropdownClasses = `${dropdownBaseClasses} ${dropdownAnimationClasses} ${dropdownHiddenClasses} ${dropdownColorClasses[color]} ${contentClassName}`

  // Рендер триггера
  const renderDefaultTrigger = () => {
    // Когда arrowIcon=false и кастомный label - используем компактные стили, но если явно указан w-full в className - используем полную ширину
    const forceFullWidth = className.includes("w-full")
    const buttonClasses =
      useCompactWidth && !forceFullWidth
        ? "inline-flex items-center text-sm cursor-pointer"
        : isCustomLabel
          ? "flex items-center justify-between w-full text-sm cursor-pointer"
          : "flex items-center justify-between w-full px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-4 focus:ring-gray-200 cursor-pointer"

    const labelClasses =
      useCompactWidth && !forceFullWidth ? "text-left overflow-hidden" : "flex-1 text-left overflow-hidden min-w-0"

    return (
      <button
        ref={triggerRef as React.RefObject<HTMLButtonElement>}
        type="button"
        className={buttonClasses}
        onClick={trigger === "click" ? handleToggle : undefined}
        onMouseEnter={trigger === "hover" ? handleMouseEnter : undefined}
        onMouseLeave={trigger === "hover" ? handleMouseLeave : undefined}
        disabled={disabled}
      >
        <span className={labelClasses}>{label}</span>
        {arrowIcon && (
          <ChevronDownIcon
            className="shrink-0 ml-1"
            size={20}
          />
        )}
      </button>
    )
  }

  // Рендер кастомного триггера
  const renderCustomTrigger = () => {
    if (!renderTrigger) return null

    return renderTrigger({
      ref: triggerRef as React.Ref<HTMLElement>,
      onClick: trigger === "click" ? handleToggle : () => {},
      disabled,
    })
  }

  // Recursively process children to add close handler to all CDropdownItem elements
  // This handles CDropdownItem elements inside fragments and conditional blocks
  const processChildren = (children: ReactNode): ReactNode => {
    return React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) {
        return child
      }

      // If it's a CDropdownItem, add the close handler
      if (child.type === CDropdownItem) {
        const typedChild = child as React.ReactElement<CDropdownItemProps>
        return React.cloneElement(typedChild, {
          onClick: (event: React.MouseEvent) => {
            typedChild.props.onClick?.(event)
            if (dismissOnClick) {
              handleItemClick()
            }
          },
        })
      }

      // If it's a fragment or has children, recursively process them
      const childProps = child.props as { children?: ReactNode }
      if (childProps.children) {
        return React.cloneElement(child as React.ReactElement<{ children?: ReactNode }>, {
          children: processChildren(childProps.children),
        })
      }

      return child
    })
  }

  return (
    <Component
      ref={ref}
      className={finalClasses}
      id={id}
      style={style}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...rest}
    >
      {/* Триггер */}
      {renderTrigger ? renderCustomTrigger() : renderDefaultTrigger()}

      {/* Overlay и Dropdown контент в одном портале - only for non-inline mode */}
      {open &&
        !inline &&
        createPortal(
          <div
            className="fixed inset-0"
            style={{ zIndex }}
          >
            {/* Overlay слой для блокировки других действий */}
            <div
              // Not a control: with the mouse it repeats the closing that Escape already does.
              role="presentation"
              ref={overlayRef}
              className="absolute inset-0 opacity-0"
              onMouseDown={handleClose}
            />

            {/* Dropdown контент */}
            <div
              // Not a control: a floating panel whose handler only keeps the press off the overlay.
              role="presentation"
              ref={dropdownRef}
              className={`${dropdownClasses} absolute`}
              style={
                {
                  ...dropdownPosition,
                  zIndex: zIndex + 1,
                  transformOrigin: getTransformOrigin(),
                  animationFillMode: "backwards",
                  transition: "none",
                  "--trigger-width": `${triggerWidth}px`,
                } as React.CSSProperties
              }
              onMouseEnter={trigger === "hover" ? handleMouseEnter : undefined}
              onMouseLeave={trigger === "hover" ? handleMouseLeave : undefined}
              onMouseDown={(event) => {
                // Предотвращаем всплытие события к overlay
                event.stopPropagation()
              }}
            >
              {processChildren(children)}
            </div>
          </div>,
          document.body,
        )}

      {/* Inline dropdown - рендерим через portal для корректной работы с overlay */}
      {inline &&
        open &&
        createPortal(
          <div
            className="fixed inset-0"
            style={{ zIndex }}
          >
            {/* Overlay для блокировки кликов */}
            <div
              // Not a control: with the mouse it repeats the closing that Escape already does.
              role="presentation"
              className="absolute inset-0"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                handleClose()
              }}
            />
            {/* Dropdown контент */}
            <div
              // Not a control: a floating panel whose handler only stops the click from travelling.
              role="presentation"
              ref={dropdownRef}
              className={`${dropdownClasses} absolute`}
              style={
                {
                  ...dropdownPosition,
                  zIndex: zIndex + 1,
                  transformOrigin: getTransformOrigin(),
                  animationFillMode: "backwards",
                  transition: "none",
                  "--trigger-width": `${triggerWidth}px`,
                } as React.CSSProperties
              }
              onMouseEnter={trigger === "hover" ? handleMouseEnter : undefined}
              onMouseLeave={trigger === "hover" ? handleMouseLeave : undefined}
              onClick={(event) => event.stopPropagation()}
            >
              {processChildren(children)}
            </div>
          </div>,
          document.body,
        )}
    </Component>
  )
})

CDropdownComponent.displayName = "CDropdown"

// Создаем компонент с статическими свойствами
const CDropdownWithStatics = Object.assign(CDropdownComponent, {
  Item: CDropdownItem,
  Header: CDropdownHeader,
  Divider: CDropdownDivider,
})

// Exported under its own identifier: Fast Refresh (react-refresh/only-export-components)
// recognises a component by the name, and the Object.assign that attaches the statics is opaque to it.
export const CDropdown = CDropdownWithStatics
